import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import usePrevious from './usePrevious';
import { getEvents, cleanEvents } from '../store/actions/events';

const applyDateSerializer = ({ date, ...values }) => {
  if (date && date.length) {
    const [minDate, maxDate] = date;

    return {
      ...values,
      minDate: minDate.format('YYYY-MM-DD HH:mm:ss'),
      maxDate: maxDate.format('YYYY-MM-DD HH:mm:ss'),
    };
  }
  return values;
};

const deserializeFilter = data => {
  const parsed = JSON.parse(data);
  if (!parsed) {
    return;
  }
  const { date, ...values } = parsed;

  // eslint-disable-next-line consistent-return
  return {
    date: date && date.map(item => moment(item)),
    ...values,
  };
};

/**
 * lazy load can be used only as singleton because we connect it directly to localstorage
 * If it is necessary, the connection to the store can be moved to the data provider
 */

const getDefaultSort = () => {
  const local = JSON.parse(localStorage.getItem('sort'));

  return (
    local || {
      field: '',
      order: '',
    }
  );
};

const getDefaultFilters = () => {
  const local = deserializeFilter(localStorage.getItem('filters'));
  return local || {};
};

const useLazyLoad = () => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(getDefaultSort());
  const [filters, setFilters] = useState(getDefaultFilters());
  const [isLoading, setPageLoading] = useState(false);

  const dispatch = useDispatch();

  const previousPage = usePrevious(page);

  const load = () => {
    if (isLoading) {
      return;
    }

    setPageLoading(true);
    dispatch(getEvents({ ...applyDateSerializer(filters), sort, page })).then(
      () => {
        setPageLoading(false);
      },
    );
  };

  useEffect(() => {
    dispatch(cleanEvents());
    setPage(1);
    if (previousPage === 1) {
      load();
    }
  }, [sort, filters]);

  useEffect(() => {
    load();
  }, [page]);

  const moveToNextPage = () => {
    setPage(page => page + 1);
  };

  const updateFilters = filters => {
    setFilters(filters);
    localStorage.setItem('filters', JSON.stringify(filters));
  };

  const resetFilters = () => {
    setFilters({});
    localStorage.setItem('filters');
  };

  const updateSort = (field, order) => {
    const converted = { field, order };

    setSort({ field, order });
    localStorage.setItem('sort', JSON.stringify(converted));
  };

  return {
    filters,
    sort,
    page,
    moveToNextPage,
    updateFilters,
    resetFilters,
    updateSort,
    isLoading,
  };
};

export default useLazyLoad;
